import {Component, DoCheck, OnInit} from '@angular/core';
import {Constants} from '../../shared/Helpers/constants';
import {SharedService} from '../../shared/shared.service';
import {ActivatedRoute, Router} from '@angular/router';
import {TwoFAService, UserService} from '../../_services';
import {forkJoin, of} from 'rxjs';
import {HttpResponse} from '@angular/common/http';
import { ISmsAuthenticatorPatch, IUser2FAConfiguration, IUser2FAConfigurationPost } from 'src/app/shared/interfaces';
import { catchError } from 'rxjs/operators';
import { Alert, AlertTypeEnum } from 'src/app/shared/Helpers/Alert';

@Component({
  moduleId: module.id,
  templateUrl: './twoFA-setup.component.html'
})
export class TwoFASetupComponent implements OnInit, DoCheck {
  public logoPath: any;
  public viewToDisplay: string;
  public twoFAMethod: string;
  public userConfig: IUser2FAConfiguration | any;
  public smsEnabled: boolean;
  public methodSetup: string = '';
  public changeDevice: boolean;
  public removeLogo: boolean = false;

  constructor(
    public sharedService: SharedService,
    public userService: UserService,
    public router: Router,
    public route: ActivatedRoute,
    public twoFAService: TwoFAService
  ) {
    this.changeDevice = this.route.snapshot.queryParams['setup'] == 'change device' ? true : false
  }

  ngOnInit() {
    this.checkIfUserLoggedIn();
  }

  changeView(view: string) {
    this.viewToDisplay = view;
  }

  ngDoCheck() {
    if (this.sharedService.loadedOrganisationByUrl.value) {
      if (this.sharedService.loginCustomisation.value != undefined) {
        if (this.sharedService.LogoImage.value != null) {
          this.logoPath = null;
          this.logoPath = this.sharedService.LogoImage.value;
        }
        else {
          this.logoPath = Constants.logoPath;
        }
      }
      else {
        this.logoPath = Constants.logoPath;
      }
    }
    else {
      this.logoPath = Constants.logoPath;
    }
  }

  public checkIfUserLoggedIn() {
    if (this.sharedService.currentOrganisationId.value == null || this.sharedService.userAttemptedLogin.value == null) {
      this.router.navigateByUrl('/login');
    }
    else {
      this.getUsefulData();
    }
  }

  private getUsefulData() {
    let userConfError: any = null;
    const smsConf = this.userService.getSmsEnabled(this.sharedService.currentOrganisationId.value);
    const userConf = this.twoFAService.getUser2FAConfiguration(this.sharedService.currentOrganisationId.value, this.sharedService.userAttemptedLogin.value).pipe(catchError((err) => {
        userConfError = err.error;
        return of(err.error);
    }));

    forkJoin([smsConf, userConf]).subscribe((resp: any) => {
      if (resp[0].body) {
        this.smsEnabled = resp[0].body;
      }
      if (resp[1] instanceof HttpResponse) {
        this.userConfig = resp[1].body;
        if (this.changeDevice) {
          this.changeView('appAuthentication');
        }
        else {
          this.customizeView(this.userConfig);
        }
      }
      else if (userConfError && userConfError.errorCode == 1016) {
        this.userConfig = null;
        if (this.changeDevice) {
          this.changeView('appAuthentication');
        }
        else {
          this.customizeView(this.userConfig);
        }
      }
    })
  }

  public getUser2FAConfiguration(findView = true, explicitView = '') {
    this.twoFAService.getUser2FAConfiguration(this.sharedService.currentOrganisationId.value, this.sharedService.loggedUserId.value).subscribe({
      next: (res) => {
        this.userConfig = res.body;
        if (findView) {
          this.customizeView(this.userConfig);
        }
        else {
          this.changeView(explicitView);
        }
      },
      error: (err) => {
        // the configuration object does not exist
        if (err.error.errorCode == 1016) {
          this.userConfig = null;
          if (findView) {
            this.customizeView(this.userConfig);
          }
          else {
            this.changeView(explicitView);
          }
        }
      }
    })
  }

  private customizeView(userConfig: IUser2FAConfiguration) {
    if (userConfig) {
      // if the authorisation status is pending and user has clicked to reconfigure, it is the same as if he never tried
      // none method set up, but at least one was attempted and configuration object is created in server
      if ((userConfig.appAuthenticator == null || this.userConfig.appAuthenticator.authorisationStatus === 'PENDING') && userConfig.smsAuthenticator == null) {
        if (this.smsEnabled) {
          this.changeView('chooseAuthMethod');
        }
        else {
          this.changeView('appAuthentication');
        }
        this.methodSetup = ''
      }
      // it is not first login, another method is available
      else if (userConfig.smsAuthenticator == null) {
        if (this.smsEnabled) {
          this.changeView('smsAuthentication');
        }
        else {
          Alert.basicNotification(AlertTypeEnum.Error, "", "SMS is not configured for your organisation");
        }
        this.methodSetup = 'APP';
      }
      // it is not first login, another method is available
      else if (userConfig.appAuthenticator == null || this.userConfig.appAuthenticator.authorisationStatus === 'PENDING') {
        this.changeView('appAuthentication')
        if (this.smsEnabled) {
          this.methodSetup = 'SMS'
        }
        else {
          this.methodSetup = ''
        }
      }
      else {
        this.methodSetup = 'BOTH'
      }
    }
    // no configuration done
    else {
      if (this.smsEnabled) {
        this.changeView('chooseAuthMethod')
      }
      else {
        this.changeView('appAuthentication')
      }
      this.methodSetup = '';
    }
  }

  public authMethodChosen(method) {
    this.twoFAMethod = method
    if (method == 'APP') {
      this.changeView('appAuthentication');
    }
    else if (method == 'SMS') {
      this.changeView('smsAuthentication');
    }
  }

  sendCodeViaSms(mobileNumber) {
    this.changeView('authenticate');
    if (this.userConfig) {
      this.changeSmsConfig(mobileNumber);
    }
    else {
      this.setup2FAConfig(mobileNumber);
    }
  }

  onBack() {
    if (this.methodSetup != "") {
      this.router.navigateByUrl('/2FA-authentication')
    }
    else {
      this.customizeView(this.userConfig);
    }
  }

  private changeSmsConfig(mobileNumber) {
    const smsAuthenticatorPatchDto: ISmsAuthenticatorPatch = {
      phoneNumber: mobileNumber,
      version: 0
    }
    this.twoFAService.changeSmsConfiguration(this.sharedService.currentOrganisationId.value, this.sharedService.loggedUserId.value, this.userConfig.id, smsAuthenticatorPatchDto)
      .subscribe({
        next: (res) => {
          this.userConfig = res.body;
          this.sendCode('SMS');
        },
        error: (err) => {
          if (err?.error?.errorCode === 1000) {
            Alert.basicNotification(AlertTypeEnum.Error, "", err.error.detailedMessage);
          }
        }
    });
  }

  private setup2FAConfig(mobileNumber) {
    const user2FAConfigDto: IUser2FAConfigurationPost = {
      enabled: true,
      smsAuthenticator: {
        phoneNumber: mobileNumber,
        version: 0
      },
      typeEnabled: 'SMS'
    }
    this.twoFAService.setupUser2FAConfiguration(this.sharedService.currentOrganisationId.value, this.sharedService.loggedUserId.value, user2FAConfigDto)
      .subscribe(resp => {
        this.userConfig = resp.body;
        this.sendCode('SMS');
      });
  }

  private sendCode(viaMethod = 'SMS') {
    this.twoFAService.resend2FACode(this.sharedService.currentOrganisationId.value, this.sharedService.loggedUserId.value, viaMethod).subscribe({
      next: () => {
        this.getUser2FAConfiguration(false, 'authenticate');
      },
      error: (err) => {
        let msg = err?.error?.detailedMessage;
        if (msg.indexOf('for user') > 0) {
          msg = msg.substr(0, msg.indexOf('for user'))
        }
        Alert.basicNotification(AlertTypeEnum.Error, "", msg);
      }
    })
  }

  qrCodeScanned() {
    this.getUser2FAConfiguration(false, 'authenticate');
  }

  goToSetup() {
    this.getUser2FAConfiguration();
  }

  public removeLogoFromView(view: any) {
    this.removeLogo = view;
  }

  public sendVerificationCode() {
    this.router.navigate(['/2FA-authentication'], {queryParams: {sendCode: true}})
  }

  public showDeviceConfirmPage() {
    this.changeView('deviceConfirmed');
  }
}
